import { Component, HostListener, Inject, Input, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { ObjectType } from '@vendasta/auxiliary-data-components';
import { ProductAnalyticsService } from '@vendasta/product-analytics';
import { BehaviorSubject, combineLatest, Observable, Subject, of } from 'rxjs';
import { filter, map, startWith, takeUntil, tap, withLatestFrom } from 'rxjs/operators';
import { ComponentCanDeactivate } from './pending-changes-guard.service';
import { AuxiliaryDataPanelComponent } from './auxiliary-data-panel/auxiliary-data-panel.component';
import { AuxiliaryDataFieldInterface, AuxiliaryDataTableService } from './auxiliary-data-table.service';
import { FieldSelectedForEditService } from './columns/field-selected-for-edit.service';
import { ObjectTypeOption, objectTypeOptions as _objectTypeOptions } from './object-type-options';
import { SELECTED_OBJECT_TYPE$$, VIEWING_ARCHIVED$$ } from './tokens';
import { AuxDrawerComponent } from './side-panel';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'aux-auxiliary-data-page',
  templateUrl: './auxiliary-data-page.component.html',
  styleUrls: ['./auxiliary-data-page.component.scss'],
  standalone: false,
})
export class AuxiliaryDataPageComponent extends ComponentCanDeactivate implements OnInit, OnDestroy {
  @ViewChild('fieldEditorPanel', { static: true }) fieldEditorPanel: TemplateRef<any>;
  @ViewChild('auxiliaryDataPanelComponent', { static: false }) panelComponent: AuxiliaryDataPanelComponent;
  @ViewChild('drawer', { static: false }) drawer?: AuxDrawerComponent;

  @Input() set objectType(value: ObjectType) {
    this.selectObjectType(value);
  }

  creatingNewField$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  objectTypeOptions: ObjectTypeOption[] = _objectTypeOptions;
  private rowClicked$$ = new Subject<string>();
  private destroyed$$ = new Subject<void>();

  activeTabIndex$: Observable<number>;
  showCreateFieldButton$: Observable<boolean> = combineLatest([
    this.tableService.loading$$,
    this.tableService.totalResults$,
  ]).pipe(
    map(([loading, total]) => {
      return !loading && total > 0;
    }),
    startWith(false),
  );

  constructor(
    @Inject(VIEWING_ARCHIVED$$) readonly viewingArchived$$: BehaviorSubject<boolean>,
    @Inject(SELECTED_OBJECT_TYPE$$) readonly objectType$$: BehaviorSubject<ObjectType>,
    private readonly fieldSelectedForEditService: FieldSelectedForEditService,
    private readonly trackingService: ProductAnalyticsService,
    readonly tableService: AuxiliaryDataTableService,
    private readonly translateService: TranslateService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.fieldSelectedForEditService.editClicked$
      .pipe(
        takeUntil(this.destroyed$$),
        tap(() => this.creatingNewField$$.next(false)),
        tap(() => this.openEditDrawer()),
        tap(() =>
          this.trackingService.trackEvent(
            'CustomFields:open-edit-custom-field-panel',
            'CustomFields',
            'open-edit-custom-field-panel',
            0,
            {
              objectType: this.objectType$$.value,
            },
          ),
        ),
      )
      .subscribe();

    this.rowClicked$$
      .asObservable()
      .pipe(
        takeUntil(this.destroyed$$),
        withLatestFrom(of(this.drawer?.drawerContentRef)),
        // only contunue if the side panel is closed to not mess with the information on the opened panel
        filter(([_, currentTemplate]) => !currentTemplate),
        map(([fieldId, _]) => fieldId),
        tap((fieldId) => this.fieldSelectedForEditService.editClickedForField(fieldId)),
      )
      .subscribe();
    this.activeTabIndex$ = this.viewingArchived$$.pipe(map((isViewArchived) => (isViewArchived ? 1 : 0)));
  }

  openEditDrawer() {
    if (this.drawer && this.fieldEditorPanel) {
      this.drawer.open(this.fieldEditorPanel, this.translateService.instant('COMMON.AUXILIARY_DATA.EDIT_HEADER'));
    }
  }
  openCreateDrawer() {
    if (this.drawer && this.fieldEditorPanel) {
      this.drawer.open(this.fieldEditorPanel, this.translateService.instant('COMMON.AUXILIARY_DATA.CREATE_HEADER'));
    }
  }

  ngOnDestroy(): void {
    this.destroyed$$.next();
  }

  selectObjectType(type: ObjectType): void {
    this.objectType$$.next(type);

    this.trackingService.trackEvent('CustomFields:select-object-type', 'CustomFields', 'select-object-type', 0, {
      objectType: type,
    });
    this.tableService.reload();
  }

  archivedOrNotChange(event: { index: number }): void {
    switch (event.index) {
      case 1:
        this.viewingArchived$$.next(true);
        break;
      default:
        this.viewingArchived$$.next(false);
    }
    this.tableService.reload();
  }

  createNew(): void {
    this.creatingNewField$$.next(true);
    this.fieldSelectedForEditService.editClickedForField(null);
    this.openCreateDrawer();
    this.trackingService.trackEvent(
      'CustomFields:open-create-custom-field-panel',
      'CustomFields',
      'open-create-custom-field-panel',
      0,
      {
        objectType: this.objectType$$.value,
      },
    );
  }

  rowClicked(row: AuxiliaryDataFieldInterface): void {
    if (row?.id && !row?.archived) {
      this.rowClicked$$.next(row.id);
    }
  }

  canDeactivate(): boolean {
    if (!this.panelComponent) {
      return true;
    }
    return this.panelComponent.canDeactivate();
  }

  @HostListener('keyup', ['$event']) onKeyUp(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.panelComponent.cancel();
    }
  }
}
